import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, Circle, Clock, Star, Video, FileText, Link as LinkIcon, Download } from "lucide-react";
import empApi from "../../lib/empApi";

const TYPE_ICON = { PDF: FileText, VIDEO: Video, LINK: LinkIcon, DOC: FileText };

export default function EmpTraining() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => { empApi.get("/employee/training").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (id) => {
    setBusy(id);
    try { await empApi.post(`/employee/training/${id}/complete`); load(); } finally { setBusy(null); }
  };

  const open = async (t) => {
    if (t.type !== "PDF" && t.url) { window.open(t.url, "_blank", "noopener"); return; }
    if (!t.has_file) return;
    setBusy(t.id);
    try {
      const res = await empApi.get(`/employee/training/${t.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch { /* noop */ } finally { setBusy(null); }
  };

  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  return (
    <div className="space-y-4" data-testid="emp-training-page">
      <h2 className="font-heading text-lg font-bold text-white">Training Center</h2>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4 text-center">
          <p className="font-heading text-2xl font-bold text-white">{d.completed}/{d.total}</p>
          <p className="text-xs text-slate-400">Total Selesai</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4 text-center">
          <p className="font-heading text-2xl font-bold text-emerald-400">{d.mandatory_completed}/{d.mandatory_total}</p>
          <p className="text-xs text-slate-400">Wajib Selesai</p>
        </div>
      </div>

      <div className="space-y-2.5" data-testid="emp-training-list">
        {d.items.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Belum ada training yang ditugaskan.</p>}
        {d.items.map((t) => {
          const Icon = TYPE_ICON[t.type] || FileText;
          return (
            <div key={t.id} className="rounded-xl border border-slate-800 bg-slate-800/40 p-3.5" data-testid={`emp-training-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2.5">
                  <div className="rounded-lg bg-amber-500/15 p-2 text-amber-300"><Icon size={16} /></div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-white">{t.title}</p>
                      {t.mandatory && <span className="flex items-center gap-0.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-300"><Star size={9} /> Wajib</span>}
                    </div>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><Clock size={11} /> {t.duration_min} mnt · {t.category} · {t.type}</p>
                  </div>
                </div>
                {t.completed && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-400"><CheckCircle2 size={16} /> Selesai</span>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                {(t.url || t.has_file) && (
                  <button onClick={() => open(t)} disabled={busy === t.id} data-testid={`emp-training-open-${t.id}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-700 py-2 text-sm font-semibold text-slate-200 disabled:opacity-60">
                    {busy === t.id ? <Loader2 size={14} className="animate-spin" /> : (t.type === "PDF" ? <Download size={14} /> : <Video size={14} />)} Buka Materi
                  </button>
                )}
                {!t.completed && (
                  <button onClick={() => complete(t.id)} disabled={busy === t.id} data-testid={`emp-training-complete-${t.id}`}
                    className="flex items-center gap-1 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-3 py-2 text-sm font-bold text-slate-900 disabled:opacity-60">
                    {busy === t.id ? <Loader2 size={12} className="animate-spin" /> : <Circle size={12} />} Tandai Selesai
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
